import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Building2, Loader2 } from "lucide-react";

import { formatDateTime, errorText } from "./format";
import OrgSelect from "./OrgSelect";
import {
  type HistoryRecord,
  type SignalAdminOrganization,
  TIER_META,
} from "./types";
import { clickContextText } from "../../Pages/MyPortfolioAccounts/signalCTAs";
import { resolutionReasonLabel } from "../../Pages/MyPortfolioAccounts/signalCTAs";
import { useAppDispatch } from "../../Store/Hooks";
import { fetchSignalAdminActionHistory } from "../../Store/Reducers/SignalAdminSlice";

interface ActionHistoryPanelProps {
  orgId: string;
  organizations: SignalAdminOrganization[];
  orgsLoading: boolean;
  onOrgChange: (orgId: string) => void;
}

const EVENT_LABEL: Record<string, string> = {
  generated: "Generated",
  delivered: "Delivered",
  viewed: "Viewed",
  cta_clicked: "CTA clicked",
  snoozed: "Snoozed",
  dismissed: "Dismissed",
  resolved: "Resolved",
  resolved_after_action: "Resolved after action",
  guide_step: "Guide step",
};

const STATE_LABEL: Record<string, string> = {
  active: "Open",
  acknowledged: "Handled",
  snoozed: "Snoozed",
  resolved: "Resolved",
};

const selectClass =
  "h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-[12.5px] text-gray-800 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20";

const ActionHistoryPanel: React.FC<ActionHistoryPanelProps> = ({
  orgId,
  organizations,
  orgsLoading,
  onOrgChange,
}) => {
  const [records, setRecords] = useState<HistoryRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lens, setLens] = useState<"account" | "persona">("account");
  const [filter, setFilter] = useState("");

  const dispatch = useAppDispatch();
  const load = useCallback(async () => {
    if (!orgId) {
      setRecords([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await dispatch(fetchSignalAdminActionHistory(orgId)).unwrap();
      setRecords(res?.records ?? []);
    } catch (e) {
      setError(errorText(e, "Couldn't load action history."));
    } finally {
      setLoading(false);
    }
  }, [dispatch, orgId]);

  useEffect(() => {
    setLens("account");
    setFilter("");
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const options = useMemo(() => {
    if (lens === "account") {
      const seen = new Map<string, string>();
      records.forEach(r => seen.set(r.account_id, r.account_name));
      return Array.from(seen.entries()).sort((a, b) =>
        a[1].localeCompare(b[1])
      );
    }
    const personas = new Set<string>();
    records.forEach(r => r.personas.forEach(p => personas.add(p)));
    return Array.from(personas)
      .sort()
      .map(p => [p, p.toUpperCase()] as [string, string]);
  }, [records, lens]);

  const shown = records.filter(r =>
    !filter
      ? true
      : lens === "account"
        ? r.account_id === filter
        : r.personas.includes(filter)
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-[15px] font-bold text-gray-900">
              Action History
            </h3>
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-500">
              Per organization
            </span>
          </div>
          <p className="mt-0.5 max-w-3xl text-[12.5px] text-gray-500">
            The lifecycle of every signal in the selected organization —
            generated, viewed, acted on, snoozed, dismissed, resolved — and who
            did it. Read-only and available in every mode.
          </p>
        </div>
        <OrgSelect
          organizations={organizations}
          value={orgId}
          onChange={onOrgChange}
          loading={orgsLoading}
          emptyLabel="Select organization…"
        />
      </div>

      {!orgId ? (
        <div className="flex flex-col items-center rounded-xl border border-dashed border-gray-300 bg-gray-50/60 px-6 py-14 text-center">
          <Building2 className="h-8 w-8 text-gray-300" aria-hidden />
          <p className="mt-3 text-[14px] font-semibold text-gray-800">
            Select an organization
          </p>
          <p className="mt-1 max-w-sm text-[13px] text-gray-500">
            Action History is stored per organization. Platform policy does not
            require a selection.
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
              {(["account", "persona"] as const).map(l => (
                <button
                  key={l}
                  type="button"
                  onClick={() => {
                    setLens(l);
                    setFilter("");
                  }}
                  className={`rounded-md px-3 py-1.5 text-[12.5px] font-semibold ${
                    lens === l
                      ? "bg-white text-brand-dark shadow-sm ring-1 ring-brand/30"
                      : "text-gray-500 hover:text-gray-800"
                  }`}
                >
                  By {l}
                </button>
              ))}
            </div>
            <select
              aria-label={lens === "account" ? "Account" : "Persona"}
              value={filter}
              onChange={e => setFilter(e.target.value)}
              className={selectClass}
            >
              <option value="">
                All {lens === "account" ? "accounts" : "personas"}
              </option>
              {options.map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
            <span className="ml-auto text-xs text-gray-500">
              {loading ? "Loading…" : `${shown.length} signals`}
            </span>
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
              {error}
            </div>
          )}

          <section className="rounded-xl border border-gray-200 bg-white px-5 py-4">
            {loading && records.length === 0 ? (
              <div className="flex items-center justify-center gap-2 py-8 text-[12.5px] text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Loading history…
              </div>
            ) : shown.length === 0 ? (
              <p className="py-8 text-center text-[12.5px] text-gray-500">
                No signal activity recorded.
              </p>
            ) : (
              <ol className="m-0 list-none p-0">
                {shown.map(r => (
                  <li
                    key={r.firing_id}
                    className="relative ml-2 border-l border-gray-200 pb-5 pl-5 last:pb-0"
                  >
                    <span
                      className="absolute -left-[4px] top-1.5 h-[7px] w-[7px] rounded-full bg-brand"
                      aria-hidden
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`rounded px-1.5 py-0.5 text-[10.5px] font-bold ${TIER_META[r.tier]?.className ?? ""}`}
                      >
                        {TIER_META[r.tier]?.label ?? r.tier}
                      </span>
                      <span className="text-[13px] font-bold text-gray-900">
                        {r.title}
                      </span>
                      <span className="text-[12px] text-gray-500">
                        {r.account_name}
                      </span>
                      <span className="ml-auto rounded bg-gray-100 px-1.5 py-0.5 text-[10.5px] font-semibold text-gray-600">
                        {STATE_LABEL[r.state] ?? r.state}
                        {r.resolution_reason
                          ? ` · ${resolutionReasonLabel(r.resolution_reason)}`
                          : ""}
                      </span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1">
                      {r.events.map((e, i) => (
                        <React.Fragment key={`${e.event}-${e.at}-${i}`}>
                          {i > 0 && (
                            <span className="text-[10px] text-gray-300">→</span>
                          )}
                          <span
                            title={formatDateTime(e.at)}
                            className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600"
                          >
                            {EVENT_LABEL[e.event] ?? e.event}
                            {e.actor ? ` · ${e.actor}` : ""}
                            {e.persona ? ` (${e.persona.toUpperCase()})` : ""}
                            {e.cta_label ? ` · “${e.cta_label}”` : ""}
                            {clickContextText(e)}
                          </span>
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="mt-1 text-[11px] text-gray-400">
                      First fired {formatDateTime(r.events[0]?.at)}
                      {r.rule_version ? ` · rules ${r.rule_version}` : ""}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default ActionHistoryPanel;
